import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { itemByLanguage } from '@/utils/language';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { queryClient } from '@/services';
import { IntlCategoriesDTO } from '@/services/content-api-types';

import { getAdminState, useFocusAdminState } from '../../state';

import {
  deleteCategoryByCountryCodeAndId,
  getCategoryByCountryCodeQueryKey,
} from '@/_generated/api-clients/content';

const DeleteModal: React.FC = () => {
  const { t } = useTranslation();
  const { deleteModalState, setDeleteModalState } =
    useFocusAdminState('deleteModalState');
  const { selectedCountry } = useFocusAdminState('selectedCountry');

  const categoriesQueryKey = getCategoryByCountryCodeQueryKey({
    query: {
      isAdmin: true,
      country: selectedCountry.label,
    },
  });

  const { mutate: deleteCategory } = useMutation({
    mutationFn: ({ countryCode, id }: { countryCode: string; id: string }) =>
      deleteCategoryByCountryCodeAndId({
        path: {
          country: countryCode,
          id,
        },
      }),
    onMutate: async category => {
      await queryClient.cancelQueries({ queryKey: categoriesQueryKey });

      const previousCategories =
        queryClient.getQueryData<IntlCategoriesDTO>(categoriesQueryKey);

      const newCategories = previousCategories
        ? { ...previousCategories }
        : undefined;

      if (newCategories) {
        newCategories.categories = newCategories.categories?.filter(
          cat => cat.id !== category.id,
        );
        newCategories.featured = newCategories.featured?.filter(
          cat => cat.id !== category.id,
        );
      }

      queryClient.setQueryData(categoriesQueryKey, newCategories);

      return { previousCategories };
    },
    onSuccess: () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {t('Admin.deleteCategoryToast', {
            categoryName: itemByLanguage(deleteModalState.category?.name, ''),
          })}
        </ToastMessageBlock>
      ));
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(categoriesQueryKey, context?.previousCategories);
      console.error(err);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST
            ? 'Delete Category Fail In Cypress Test'
            : err.message}
        </ToastMessageBlock>
      ));
      datadogRum.addError(err);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: categoriesQueryKey }),
  });

  const handleDelete = async () => {
    const { selectedCountry } = getAdminState();
    const { category } = deleteModalState;

    if (!category?.id) {
      console.error('No category id');
      return;
    }

    deleteCategory({ countryCode: selectedCountry.label, id: category.id });
    setDeleteModalState(prev => ({ ...prev, open: false }));
  };

  return (
    <ConfirmationModal
      bodyText={t('Generic.deleteObject', {
        name: itemByLanguage(deleteModalState.category?.name, ''),
        object: deleteModalState.isFeatured
          ? t('Admin.recommendedCategory').toLowerCase()
          : t('Admin.category').toLowerCase(),
      })}
      headerText={t('Generic.deleteHeader', {
        type: deleteModalState.isFeatured
          ? t('Admin.recommendedCategory')
          : t('Admin.category'),
      })}
      isOpen={deleteModalState.open}
      onClose={() => setDeleteModalState(prev => ({ ...prev, open: false }))}
      primaryButtonHandler={handleDelete}
      primaryButtonText={t('Button.delete')}
      primaryButtonColor="primary"
      secondaryButtonHandler={() =>
        setDeleteModalState(prev => ({ ...prev, open: false }))
      }
      secondaryButtonText={t('Button.cancel')}
    />
  );
};

export default DeleteModal;
